import { useMemo } from 'react';

import {
  getSubscriptionPlanFeatures,
  getTierOrPlanProperty,
  isPlanFromBasicCategory,
  planFeatureIsExclusivePricingPage,
  planFeatureIsHiddenOnPricingPage,
} from 'src/data/subscription';
import { formatNumber } from 'src/lib/format';
import { useCustomerSubscriptionInfo } from 'src/store';

/**
 * Get list of features, but without "exclusive for pricing page" ones
 * @param {SUBSCRIPTION_PLAN} plan - subscription plan
 * @returns {Object[]} - list of features
 */
export const usePlanFeatures = (plan) => {
  return useMemo(() => {
    const features = getSubscriptionPlanFeatures(plan);
    return features.filter((feature) => !planFeatureIsExclusivePricingPage(feature));
  }, [plan]);
};

/**
 * Get list of features, but without "hidden for pricing page" ones
 * @param {SUBSCRIPTION_PLAN} plan - subscription plan
 * @returns {Object[]} - list of features
 */
export const usePlanFeaturesForPricing = (plan) => {
  return useMemo(() => {
    const features = getSubscriptionPlanFeatures(plan);
    return features.filter((feature) => !planFeatureIsHiddenOnPricingPage(feature));
  }, [plan]);
};

export const useDowngradePlanFeatures = (plan, tier) => {
  const { currentPlan, currentTier } = useCustomerSubscriptionInfo();

  // Subscription "words limit"
  const currentPlanFeatures = usePlanFeatures(currentPlan);
  const newPlanFeatures = usePlanFeatures(plan);

  const currentPlanWordsLimit = getTierOrPlanProperty(currentPlan, currentTier, 'wordsLimit', 0);
  const newPlanWordsLimit = getTierOrPlanProperty(plan, tier, 'wordsLimit', 0);

  const mappedFeatures = currentPlanFeatures.map((currentPlanFeature) => {
    if (currentPlanFeature.wordCreditsFeature && newPlanWordsLimit >= currentPlanWordsLimit) {
      return false;
    }

    if (currentPlanFeature.seatsLimitFeature) {
      return isPlanFromBasicCategory(plan)
        ? { text: 'Multiple users - all of your teammates will be removed' }
        : false;
    }

    const isSameFeatureInNewPlan = newPlanFeatures.some(
      (newPlanFeature) => currentPlanFeature.text === newPlanFeature.text
    );

    if (isSameFeatureInNewPlan) {
      return false;
    }

    return currentPlanFeature;
  });
  const filteredFeatures = mappedFeatures.filter((feature) => feature);

  return {
    features: filteredFeatures,
    textFormatObject: {
      wordsLimit: formatNumber(currentPlanWordsLimit),
    },
  };
};
